import { useTranslation } from 'react-i18next'

import { capabilityLabelKey, groupCapabilities } from '@/features/settings/capabilityLabels'
import type { RoleRecord } from '@/types/domain'

/* What a role gives, read-only and in words (S-7, A-5).
 *
 * The user card used to show three directory checkboxes under «Права», which
 * read as the whole of what a person may do; what their role gives was on
 * another page in another menu. This is that list, on the card: grouped by area
 * the way the role editor groups it (`groupCapabilities`), each key in the
 * catalogue's words, and the raw key for one this client has no words for — a
 * server deployed ahead still prints something a founder can look up.
 *
 * The superuser role is «Все права», never its list: the flag, not the list,
 * is what grants access, and the list is usually empty. */
export function RoleCapabilityList({ role }: { role: RoleRecord }) {
  const { t } = useTranslation()

  if (role.isSuperuser) {
    return <p className="text-text-body text-[12.5px]">{t('settings.roles.summary.allPermissions')}</p>
  }
  if (role.capabilities.length === 0) {
    return (
      <p className="text-text-muted text-[12.5px] font-light">
        {t('directories.users.roleCapabilitiesNone')}
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-2.5">
      {groupCapabilities(role.capabilities).map(([area, keys]) => (
        <div key={area} className="flex flex-col gap-1">
          <p className="asa-eyebrow">{t(`settings.roles.groups.${area}` as never, area)}</p>
          <ul className="flex flex-col gap-0.5">
            {keys.map((key) => {
              const labelKey = capabilityLabelKey(key)
              return (
                <li key={key} className="text-text-body text-[12.5px] leading-snug font-light">
                  {labelKey ? t(labelKey) : key}
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </div>
  )
}
